// packages/web/src/components/admin/email-templates/template-list-table.tsx
import { Link } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";

export interface EmailTemplateListItem {
  key: string;
  category: "transactional" | "notification" | "operational";
  filledLocales: string[];
  lastEditedAt: string | null;
  lastEditedByName: string | null;
}

const CATEGORY_LABELS: Record<EmailTemplateListItem["category"], string> = {
  transactional: "Transactioneel",
  notification: "Notificatie",
  operational: "Operationeel",
};

const CATEGORY_ORDER: EmailTemplateListItem["category"][] = [
  "transactional",
  "notification",
  "operational",
];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

interface GroupTableProps {
  category: EmailTemplateListItem["category"];
  items: EmailTemplateListItem[];
}

function GroupTable({ category, items }: GroupTableProps) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-sm font-semibold text-ink-700">{CATEGORY_LABELS[category]}</h2>
      <div className="rounded-lg border border-line overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-surface-1">
              <th className="px-4 py-2.5 text-left font-medium text-ink-500">Sjabloon</th>
              <th className="px-4 py-2.5 text-left font-medium text-ink-500">Talen ingevuld</th>
              <th className="px-4 py-2.5 text-left font-medium text-ink-500">Laatst bewerkt</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {items.map((row, idx) => (
              <tr
                key={row.key}
                className={idx < items.length - 1 ? "border-b border-line" : undefined}
              >
                <td className="px-4 py-3 font-mono text-xs text-ink-700">{row.key}</td>
                <td className="px-4 py-3">
                  {row.filledLocales.length === 0 ? (
                    <span className="italic text-ink-500">standaard</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {row.filledLocales.map((loc) => (
                        <Badge key={loc}>{loc}</Badge>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-ink-700">
                  {row.lastEditedAt ? (
                    <>
                      {formatDate(row.lastEditedAt)}
                      {row.lastEditedByName ? (
                        <span className="ml-1 text-ink-500">— {row.lastEditedByName}</span>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-ink-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to="/admin/email-templates/$templateKey"
                    params={{ templateKey: row.key }}
                    className="text-p-600 hover:underline text-sm font-medium"
                  >
                    Bewerken
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface TemplateListTableProps {
  items: EmailTemplateListItem[];
}

export function TemplateListTable({ items }: TemplateListTableProps) {
  const byCategory = Object.fromEntries(
    CATEGORY_ORDER.map((cat) => [cat, items.filter((it) => it.category === cat)]),
  ) as Record<EmailTemplateListItem["category"], EmailTemplateListItem[]>;

  return (
    <>
      {CATEGORY_ORDER.filter((cat) => byCategory[cat].length > 0).map((cat) => (
        <GroupTable key={cat} category={cat} items={byCategory[cat]} />
      ))}
    </>
  );
}
